import React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { IDevice, API } from '@getgreenline/homi-shared'
import { LoadingAnimation } from '../../../components/LoadingAnimation'
import { ResetPasswordModal } from './Devices/ResetPasswordModal'
import { ConfigureCardTerminalModal } from './Devices/ConfigureCardTerminalModal'
import { DeviceConfiguration } from './Devices/DeviceConfiguration'
import { MonerisConfigurationModal } from './Devices/MonerisConfigurationModal'
import Helmet from 'react-helmet'
import { Row } from 'antd'
import { THEME_COLORS } from '@getgreenline/shared'
import { BLAZE_COMPANY } from '../../../constants/general'

interface Props {
  currentCompanyStore: CurrentCompanyStore
}

interface State {
  devices?: IDevice[]
  selectedDevice?: IDevice
  isResetPasswordModalOpen: boolean
  isConfigureCardTerminalModalOpen: boolean
  isMonerisModalOpen: boolean
}

@inject('currentCompanyStore')
@observer
export class POSDevices extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      devices: undefined,
      selectedDevice: undefined,
      isResetPasswordModalOpen: false,
      isConfigureCardTerminalModalOpen: false,
      isMonerisModalOpen: false,
    }
  }

  componentDidMount() {
    this.loadDevices()
  }

  async loadDevices() {
    const company = this.props.currentCompanyStore!.company!
    const devices = await API.getDevices(company.id)
    this.setState({ devices })
  }

  showResetPasswordModal = (device: IDevice) => {
    this.setState({
      selectedDevice: device,
      isResetPasswordModalOpen: true,
      isConfigureCardTerminalModalOpen: false,
    })
  }

  showTerminalOptionsModal = (device: IDevice) => {
    this.setState({
      selectedDevice: device,
      isConfigureCardTerminalModalOpen: true,
      isResetPasswordModalOpen: false,
    })
  }

  showMonerisOptionsModal = (device: IDevice) => {
    this.setState({
      selectedDevice: device,
      isMonerisModalOpen: true,
      isResetPasswordModalOpen: false,
    })
  }

  render() {
    const locations = this.props.currentCompanyStore!.locations || []
    const {
      devices,
      selectedDevice,
      isResetPasswordModalOpen,
      isConfigureCardTerminalModalOpen,
      isMonerisModalOpen,
    } = this.state

    if (!devices) {
      return <LoadingAnimation />
    }

    return (
      <Row className='white-rounded-container'>
        <Helmet>
          <title>Settings | POS</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <p className='blaze-text-muted mb-4'>
          POS devices are separate logins from your Dashboard credentials.
          <br />
          To add more POS devices to your account, please contact {BLAZE_COMPANY} support.
        </p>

        {locations.map((location) => {
          const matchingDevices = devices.filter((device) => device.locationId === location.id)

          return (
            <section
              key={location.id}
              className='pb-4 mb-4 card-wrapper'
              style={{
                borderBottom: '1px solid',
                borderBottomColor: THEME_COLORS.MONO.monoee,
              }}
            >
              <h5 className='blaze-text-muted mb-4'>
                <i className='fa fa-map-marker mr-2' aria-hidden='true' />
                <span>{location.name}</span>
              </h5>

              <div className='d-flex flex-wrap'>
                {matchingDevices.length > 0 ? (
                  matchingDevices.map((device) => {
                    return (
                      <DeviceConfiguration
                        key={device.id}
                        device={device}
                        showResetPasswordModal={this.showResetPasswordModal}
                        showTerminalOptionsModal={this.showTerminalOptionsModal}
                        showMonerisOptionsModal={this.showMonerisOptionsModal}
                      />
                    )
                  })
                ) : (
                  <div className='blaze-text-muted'>This location has no assigned POS devices.</div>
                )}
              </div>
            </section>
          )
        })}

        {selectedDevice && isResetPasswordModalOpen && (
          <ResetPasswordModal
            isOpen={isResetPasswordModalOpen}
            onHide={() =>
              this.setState({
                selectedDevice: undefined,
                isResetPasswordModalOpen: false,
              })
            }
            deviceUser={selectedDevice}
          />
        )}

        {selectedDevice && isConfigureCardTerminalModalOpen && (
          <ConfigureCardTerminalModal
            onSave={() => {
              this.setState({
                selectedDevice: undefined,
                isConfigureCardTerminalModalOpen: false,
              })
              this.loadDevices()
            }}
            onHide={() => {
              this.setState({
                selectedDevice: undefined,
                isConfigureCardTerminalModalOpen: false,
              })
            }}
            device={selectedDevice}
          />
        )}

        {selectedDevice && isMonerisModalOpen && (
          <MonerisConfigurationModal
            onHide={() => {
              this.props.currentCompanyStore!.getDevices()
              this.setState({
                selectedDevice: undefined,
                isMonerisModalOpen: false,
              })
            }}
            device={selectedDevice}
          />
        )}
      </Row>
    )
  }
}
